import React, { useState, useEffect } from 'react';
import { Loader2, RefreshCw, Hash, Type, Calendar, ToggleLeft } from 'lucide-react';
import { getInfo, convertColumnType } from '../../utils/api';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import MissingChoice from './MissingChoice';

const TYPE_OPTIONS = [
  { id: 'string', nameKey: 'convert.type.string', icon: Type, descriptionKey: 'convert.type.stringHelp' },
  { id: 'integer', nameKey: 'convert.type.integer', icon: Hash, descriptionKey: 'convert.type.integerHelp' },
  { id: 'float', nameKey: 'convert.type.float', icon: Hash, descriptionKey: 'convert.type.floatHelp' },
  { id: 'datetime', nameKey: 'convert.type.datetime', icon: Calendar, descriptionKey: 'convert.type.datetimeHelp' },
  { id: 'boolean', nameKey: 'convert.type.boolean', icon: ToggleLeft, descriptionKey: 'convert.type.booleanHelp' },
];

const DATE_FORMATS = [
  { value: '', labelKey: 'convert.dateAuto' },
  { value: '%Y-%m-%d', label: 'YYYY-MM-DD (2024-01-15)' },
  { value: '%d/%m/%Y', label: 'DD/MM/YYYY (15/01/2024)' },
  { value: '%m/%d/%Y', label: 'MM/DD/YYYY (01/15/2024)' },
  { value: '%d-%m-%Y', label: 'DD-MM-YYYY (15-01-2024)' },
  { value: '%Y/%m/%d', label: 'YYYY/MM/DD (2024/01/15)' },
  { value: '%d %b %Y', label: 'DD Mon YYYY (15 Jan 2024)' },
  { value: '%B %d, %Y', label: 'Month DD, YYYY (January 15, 2024)' },
];

const ConvertTypeOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [columns, setColumns] = useState([]);
  const [columnTypes, setColumnTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  
  const [config, setConfig] = useState({
    column: '',
    target_type: '',
    date_format: ''
  });

  useEffect(() => {
    loadColumns();
  }, []);

  const loadColumns = async () => {
    try {
      const result = await getInfo();
      setColumns(result.info.column_names || []);
      // Create a map of column to type
      const types = {};
      result.info.column_names?.forEach((col, idx) => {
        types[col] = result.info.column_types?.[idx] || 'unknown';
      });
      setColumnTypes(types);
    } catch (error) {
      toast.error(t('form.loadColumnsFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!config.column) {
      toast.error(t('convert.needColumn'));
      return;
    }
    if (!config.target_type) {
      toast.error(t('convert.needType'));
      return;
    }

    try {
      setApplying(true);
      const result = await convertColumnType({
        column: config.column,
        target_type: config.target_type,
        date_format: config.date_format || null
      });
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('convert.failed'));
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 text-muted-foreground">
        <RefreshCw className="h-5 w-5" />
        <p className="text-sm">Convert a column to a different data type</p>
      </div>

      {/* Column selection */}
      <div>
        <label className="label mb-2 block">{t('convert.selectColumn')}</label>
        <select
          value={config.column}
          onChange={(e) => setConfig(prev => ({ ...prev, column: e.target.value }))}
          className="select"
          data-testid="convert-column-select"
        >
          <option value="">{t('email.selectColumn')}</option>
          {columns.map(col => (
            <option key={col} value={col}>
              {col} ({columnTypes[col] || 'unknown'})
            </option>
          ))}
        </select>
        {config.column && (
          <p className="text-xs text-muted-foreground mt-1">
            {t('convert.currentType')} <span className="font-medium">{columnTypes[config.column]}</span>
          </p>
        )}
      </div>

      {/* Target type selection */}
      <div>
        <label className="label mb-2 block">{t('convert.targetType')}</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {TYPE_OPTIONS.map(type => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => setConfig(prev => ({ ...prev, target_type: type.id }))}
                className={`p-3 rounded-lg text-left transition-all ${
                  config.target_type === type.id
                    ? 'bg-primary/10 border-2 border-primary'
                    : 'bg-muted/50 border-2 border-transparent hover:border-muted-foreground/20'
                }`}
                data-testid={`convert-type-${type.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                    config.target_type === type.id ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  }`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{t(type.nameKey)}</div>
                    <div className="text-xs text-muted-foreground">{t(type.descriptionKey)}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Date format (only for datetime) */}
      {config.target_type === 'datetime' && (
        <div>
          <label className="label mb-2 block">{t('convert.dateFormat')}</label>
          <select
            value={config.date_format}
            onChange={(e) => setConfig(prev => ({ ...prev, date_format: e.target.value }))}
            className="select"
            data-testid="convert-date-format"
          >
            {DATE_FORMATS.map(fmt => (
              <option key={fmt.value} value={fmt.value}>{fmt.labelKey ? t(fmt.labelKey) : fmt.label}</option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            {t('convert.dateHelp')}
          </p>
        </div>
      )}

      {/* Info box */}
      <div className="p-4 bg-muted/50 rounded-lg">
        <h4 className="text-sm font-medium mb-2">{t('convert.notesTitle')}</h4>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• <strong>{t('convert.noteInteger')}</strong> {t('convert.noteIntegerBody')}</li>
          <li>• <strong>{t('convert.noteFloat')}</strong> {t('convert.noteFloatBody')}</li>
          <li>• <strong>{t('convert.noteBoolean')}</strong> {t('convert.noteBooleanBody')}</li>
          <li>• <strong>{t('convert.noteDate')}</strong> {t('convert.noteDateBody')}</li>
        </ul>
      </div>

      {/* Actions */}
      <MissingChoice reasons={[!config.column && t('convert.needColumn'), !config.target_type && t('convert.needType')]} />

      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <button onClick={onCancel} className="btn-secondary" data-testid="convert-cancel">
          {t('form.cancel')}
        </button>
        <button
          onClick={handleApply}
          disabled={applying || !config.column || !config.target_type}
          className="btn-primary flex items-center gap-2"
          data-testid="convert-apply"
        >
          {applying && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('convert.apply')}
        </button>
      </div>
    </div>
  );
};

export default ConvertTypeOperation;
